import {
  BookOpen,
  Briefcase,
  Calendar,
  ChartPie,
  CircleCheck,
  ClipboardList,
  FileSearch,
  FileText,
  Layers,
  ListChecks,
  MessageSquare,
  Pencil,
  Scale,
  ScanLine,
  Shapes,
  Share2,
  ShieldAlert,
  Sparkle,
  SquarePen,
  User,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { DynamicIcon, iconNames, type IconName } from 'lucide-react/dynamic';

import type { PromptWorkType } from '@/types/prompts';

/**
 * The names the VH3 seeds and the Groups picker's short row use, bundled
 * statically: `DynamicIcon` loads each icon on demand, and in the composer
 * that left Report, Technical, Investigate and Assist blank. Any other Lucide
 * name still loads on demand, with the generic glyph while it does or if it
 * cannot.
 */
const STATIC_ICONS: Record<string, LucideIcon> = {
  layers: Layers,
  'square-pen': SquarePen,
  'chart-pie': ChartPie,
  wrench: Wrench,
  'scan-line': ScanLine,
  'share-2': Share2,
  sparkle: Sparkle,
  'file-text': FileText,
  'file-search': FileSearch,
  'book-open': BookOpen,
  'list-checks': ListChecks,
  'message-square': MessageSquare,
  pencil: Pencil,
  briefcase: Briefcase,
  'shield-alert': ShieldAlert,
  'circle-check': CircleCheck,
  scale: Scale,
  user: User,
  calendar: Calendar,
  'clipboard-list': ClipboardList,
};

const LUCIDE_NAMES = new Set<string>(iconNames);

const isLucideName = (name: string | undefined): name is IconName =>
  !!name && LUCIDE_NAMES.has(name);

const ICON_PROPS = { size: 16, strokeWidth: 1.5, 'aria-hidden': true } as const;

/** The generic glyph: an unknown name, a legacy URL that is missing, or none. */
const Fallback = () => <Shapes {...ICON_PROPS} />;

type PromptGroupIconProps = {
  /** Lucide name from the commands endpoint (switch on). */
  lucideIcon?: string;
  /** Legacy icon URL, used when the Lucide name is missing or unknown. */
  iconUrl?: string;
  /**
   * The group's type of work: `data-work` on the icon, which the stylesheet
   * tints from the agent palette (`--work-*` in index.css). Left out, the
   * icon keeps its host's ink (My prompts, the Hidden tag).
   */
  workType?: PromptWorkType;
};

/**
 * A Prompt Group's icon at 16px: Lucide by name, else the old icon URL, else
 * the generic glyph. Never blank.
 */
export function PromptGroupIcon({
  lucideIcon,
  iconUrl,
  workType,
}: PromptGroupIconProps) {
  const Static = lucideIcon ? STATIC_ICONS[lucideIcon] : undefined;
  if (Static) {
    return (
      <span
        data-lucide={lucideIcon}
        data-work={workType}
        className='prompt-group-icon'
      >
        <Static {...ICON_PROPS} />
      </span>
    );
  }

  if (isLucideName(lucideIcon)) {
    return (
      <span
        data-lucide={lucideIcon}
        data-work={workType}
        className='prompt-group-icon'
      >
        <DynamicIcon name={lucideIcon} fallback={Fallback} {...ICON_PROPS} />
      </span>
    );
  }

  // The old vault SVGs, tinted with the row's ink like ThirdPartySvg does today.
  if (iconUrl) {
    return (
      <span
        aria-hidden
        data-icon-url={iconUrl}
        data-work={workType}
        className='prompt-menu__mask'
        style={{
          mask: `url("${iconUrl}") no-repeat center / contain`,
          WebkitMask: `url("${iconUrl}") no-repeat center / contain`,
        }}
      />
    );
  }

  return (
    <span data-fallback='' data-work={workType} className='prompt-group-icon'>
      <Fallback />
    </span>
  );
}
